import React, { useEffect, useState, useCallback, useRef } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Global blocking "Must Read" alert overlay.
 * Polls /system-alerts/active for the logged-in user (staff / customer /
 * investor). While an unacknowledged alert exists it flashes a full-screen
 * overlay that grays out and blocks every function until the user
 * acknowledges. Acknowledgement is per-user and permanent.
 */
export default function SystemAlertGate() {
  const { user, getAuthHeaders } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [acking, setAcking] = useState(false);
  const timer = useRef(null);

  const fetchActive = useCallback(async () => {
    if (!user) { setAlerts([]); return; }
    try {
      const res = await fetch(`${API}/api/system-alerts/active`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (e) { /* keep showing whatever we have */ }
  }, [user, getAuthHeaders]);

  useEffect(() => {
    fetchActive();
    if (timer.current) clearInterval(timer.current);
    if (user) timer.current = setInterval(fetchActive, 25000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [user, fetchActive]);

  if (!user || alerts.length === 0) return null;

  const current = alerts[0];

  const acknowledge = async () => {
    setAcking(true);
    try {
      const res = await fetch(`${API}/api/system-alerts/${current.alert_id}/acknowledge`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (res.ok) {
        const rest = alerts.slice(1);
        setAlerts(rest);
      }
    } finally { setAcking(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      data-testid="system-alert-overlay"
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="system-alert-flash w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden border-4 border-red-500">
        <div className="bg-red-600 px-5 py-3 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-white animate-pulse" />
          <span className="text-white font-bold tracking-wide uppercase text-sm">Important — Must Read</span>
          {alerts.length > 1 && (
            <span className="ml-auto text-white/90 text-xs">1 of {alerts.length}</span>
          )}
        </div>
        {current.image_url && (
          <img src={current.image_url} alt="" className="w-full max-h-64 object-contain bg-gray-50" data-testid="system-alert-image" />
        )}
        <div className="px-6 py-5 space-y-3">
          <h2 className="text-xl font-bold text-gray-900" data-testid="system-alert-title">{current.title}</h2>
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed" data-testid="system-alert-message">{current.message}</p>
        </div>
        <div className="px-6 pb-6">
          <button
            onClick={acknowledge}
            disabled={acking}
            data-testid="system-alert-acknowledge"
            className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-red-600 hover:bg-red-700 text-white font-semibold py-3 transition-colors disabled:opacity-70"
          >
            {acking ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            I have read &amp; acknowledge
          </button>
          <p className="text-[11px] text-center text-gray-400 mt-2">You must acknowledge to continue using the app.</p>
        </div>
      </div>
    </div>
  );
}
